import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm, useWatch } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
} from 'react-hook-form-mui';
import { Card, CardContent, CardHeader, Stack } from '@mui/material';

import {
    BusinessInput,
    EditButtons,
    PourTypeInput,
    PriceTierInput,
    ProductInput,
} from '@barboxx/admin-components';
import { BUSINESS_PRODUCT_PRICE_TIER_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { BusinessProductPriceTier } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';

interface Props {
    businessProductPriceTier: BusinessProductPriceTier;
}

export const BusinessProductPriceTierDetailsCard = ({
    businessProductPriceTier,
}: Props) => {
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const mutateBusinessProductPriceTier = useMutation({
        mutationKey: ['businessProductPriceTier', businessProductPriceTier.id],
        mutationFn: (data: BusinessProductPriceTier.UpdateRequest) =>
            Api.BusinessSpecific.ProductPriceTier.putProductPriceTier(
                businessProductPriceTier.id,
                data
            ),
    });

    const formContext = useForm<BusinessProductPriceTier.UpdateRequest>({
        resolver: yupResolver(BUSINESS_PRODUCT_PRICE_TIER_SCHEMA),
        defaultValues: businessProductPriceTier,
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: BusinessProductPriceTier.UpdateRequest) => {
        mutateBusinessProductPriceTier
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated product price tier!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notifications.show('Error Updating product price tier!', {
                    severity: 'error',
                });
            });
    };

    const values = useWatch(
        formContext
    ) as BusinessProductPriceTier.UpdateRequest;

    return (
        <Card>
            <CardHeader title="BusinessProductPriceTier Details" />
            <CardContent>
                <FormContainer onSuccess={onSubmit} formContext={formContext}>
                    <Stack gap={2}>
                        <BusinessInput disabled={!editable} />
                        <ProductInput disabled={!editable} />
                        <PriceTierInput disabled={!editable} />
                        <PourTypeInput
                            disabled={!editable}
                            onChange={() => {
                                formContext.setValue('isDefault', false);
                            }}
                        />

                        {values.pourTypeId && (
                            <TextFieldElement
                                label="Price Adjustment"
                                name="priceAdjustment"
                                type="number"
                                fullWidth
                                slotProps={{
                                    htmlInput: {
                                        max: 1000,
                                        min: 0,
                                        step: 1,
                                    },
                                }}
                                disabled={!editable}
                            />
                        )}
                        <TextFieldElement
                            label="Display Order"
                            name="displayOrder"
                            type="number"
                            fullWidth
                            slotProps={{
                                htmlInput: {
                                    max: 1000,
                                    min: 0,
                                    step: 1,
                                },
                            }}
                            disabled={!editable}
                        />
                        <CheckboxElement
                            label="Active"
                            name="isActive"
                            disabled={!editable}
                        />
                        {!values.pourTypeId && (
                            <CheckboxElement
                                label="Default"
                                name="isDefault"
                                disabled={!editable}
                            />
                        )}

                        <Can I="update" a="BusinessProductPriceTier">
                            <EditButtons
                                formContext={formContext}
                                handleCancel={handleCancel}
                                editable={editable}
                                editableActions={editableActions}
                            />
                        </Can>
                    </Stack>
                </FormContainer>
            </CardContent>
        </Card>
    );
};
